import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../../../server/api/hooks/use-api-loader";
import { TwilioEngineSevice, type TwilioMessage } from "../../../../../../../server/api/twilio";
import { useToken } from "../../../../../../../server/auth/use-token";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import { useMessageBodyCreator } from "../../../../../../message/editor/utils/create-message-body/create-message-body";
import { usePreviewContext } from "../preview-context";
import { useEmailPreviewSource } from "./use-email-preview-source";
import { usePreviewTags } from "./use-preview-tags";

export function useBuiltTwilioMessage(): UseApiLoaderResponse<TwilioMessage["data"]> {
  const getToken = useToken();

  const {
    moment_preview_data: { design, account_id, dynamic_segment_id_map },
    preview_info,
    preview_test_data,
  } = usePreviewContext();

  const {
    design: previewDesign,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = useEmailPreviewSource();

  const { loading: createMessageBodyLoading, data: createMessageBody } = useMessageBodyCreator();

  const { loading: mergeFieldsLoading, data: mergeFields } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design: previewDesign,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
  });

  return useApiLoader(async () => {
    if (createMessageBodyLoading || mergeFieldsLoading) throw new Waiting();
    if (!createMessageBody) throw new Error("No message body creator");

    const token = await getToken();

    const { data: messageBodyData, errorMessage } = await createMessageBody({
      editorState: design,
      channel: "twilio",
      format: "sms",
      account_id,
    });

    if (errorMessage || !messageBodyData?.body)
      throw new Error(errorMessage ?? "Error creating message body");

    const service = new TwilioEngineSevice(token);

    const response = await service.buildMessage({
      body: messageBodyData.body,
      mail_merge_fields: {
        person_id: "preview",
        ...Object.fromEntries(
          (mergeFields?.tags ?? []).map((field) => [field.key, field.value ?? ""]),
        ),
      },
      dynamic_condition_fields: mergeFields?.dynamic_condition_fields ?? {},
    });

    if (response.error || !response.data)
      throw new Error(response.error ?? "Error building message");
    return response.data.data;
  }, [
    createMessageBodyLoading,
    mergeFieldsLoading,
    createMessageBody,
    getToken,
    design,
    account_id,
    mergeFields?.tags,
    mergeFields?.dynamic_condition_fields,
  ]);
}
